/*
 * Copyright 2026 Sony Corporation
 */

import { Box, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';

import { ACTIVITY_ITEMS, type ActivityView } from './activity-items';

export type { ActivityView };

export interface ActivityBarProps {
  active: ActivityView | undefined;
  /**
   * Entries that navigate rather than open a panel here. The Scene page has no
   * world bound to it, so its three world-scoped entries send you to pick one.
   */
  linkTo?: Partial<Record<ActivityView, string>>;
  /** Omitted on a page where every entry navigates. */
  onSelect?: (view: ActivityView | undefined) => void;
}

/**
 * The icon rail, and the only way to choose what the side bar shows.
 *
 * Clicking the open view closes it, as in the editor this borrows from: the
 * viewport is the point of the screen, and a user who wants more of it should
 * not have to find a different control to get it.
 */
export const ActivityBar = ({ active, linkTo, onSelect }: ActivityBarProps) => (
  <Box
    className="flex w-12 shrink-0 flex-col border-r"
    sx={{
      bgcolor: 'background.paper',
      borderColor: 'divider',
      // Sized here rather than per item, so a rail entry added later cannot
      // arrive a different size.
      '& .MuiSvgIcon-root': { fontSize: 26 },
    }}
  >
    {ACTIVITY_ITEMS.map((item) => {
      const to = linkTo?.[item.id];
      const selected = item.id === active;

      return (
        <Tooltip key={item.id} title={item.label} placement="right">
          <Box
            {...(to
              ? { component: Link, to, 'aria-current': selected ? ('page' as const) : undefined }
              : {
                  component: 'button' as const,
                  'aria-pressed': selected,
                  onClick: () => onSelect?.(selected ? undefined : item.id),
                })}
            aria-label={item.label}
            className="flex h-12 w-full cursor-pointer items-center justify-center border-0 border-l-2 bg-transparent"
            sx={{
              // The accent on the left edge is what says which view is open;
              // the icon only brightens, which is not enough on its own.
              borderLeftStyle: 'solid',
              borderLeftColor: selected ? 'primary.main' : 'transparent',
              color: selected ? 'text.primary' : 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            {item.icon}
          </Box>
        </Tooltip>
      );
    })}
  </Box>
);
